import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/apiClient";
import { captureUtm, trackEvent, getVisitorId } from "../lib/analytics";

const Q = [
  { key: "profile", q: "Quel est votre profil ?", opts: [["creator", "Artiste / Créateur"], ["manager", "Manager"], ["professional", "Professionnel"], ["organization", "Structure / Organisation"]] },
  { key: "stage", q: "Où en êtes-vous actuellement ?", onlyCreator: true, opts: [["debut", "Je débute"], ["publie", "Je publie déjà"], ["developpe", "Je développe activement mon projet"], ["audience", "J'ai déjà une audience"], ["professionnaliser", "Je cherche à professionnaliser mon activité"]] },
  { key: "goal", q: "Quel est actuellement votre principal objectif ?", opts: [["audience", "Développer mon audience"], ["content", "Sortir davantage de contenu"], ["structure", "Structurer ma carrière"], ["marketing", "Développer mon marketing"], ["find_pros", "Trouver des professionnels"], ["find_manager", "Trouver un manager"], ["monetize", "Monétiser mon activité"], ["multi", "Développer plusieurs projets"]] },
  { key: "blocker", q: "Qu'est-ce qui vous bloque le plus aujourd'hui ?", opts: [["strategy", "Manque de stratégie"], ["time", "Manque de temps"], ["visibility", "Manque de visibilité"], ["network", "Manque de réseau"], ["marketing", "Marketing"], ["organization", "Organisation"], ["budget", "Budget"], ["priority", "Je ne sais pas quelle action prioriser"]] },
  { key: "tools", q: "Utilisez-vous actuellement des outils pour développer votre projet ?", opts: [["plusieurs", "Oui, plusieurs"], ["quelques", "Quelques-uns"], ["peu", "Très peu"], ["aucun", "Aucun"]] },
  { key: "frequency", q: "À quelle fréquence travaillez-vous sur votre développement ?", opts: [["daily", "Tous les jours"], ["weekly", "Plusieurs fois par semaine"], ["monthly", "Quelques fois par mois"], ["occasional", "Occasionnellement"]] },
  { key: "priorities", q: "Votre priorité dans les 90 prochains jours ?", multi: true, opts: [["audience", "Développer mon audience"], ["content", "Publier plus de contenu"], ["structure", "Structurer ma carrière"], ["marketing", "Marketing"], ["find_pros", "Trouver des pros"], ["monetize", "Monétiser"], ["multi", "Plusieurs projets"]] },
  { key: "support", q: "Quel niveau d'accompagnement recherchez-vous ?", opts: [["outils", "Je veux simplement des outils"], ["guide", "Je veux être guidé"], ["automatiser", "Je veux automatiser une partie de mon développement"], ["pro", "Je veux développer mon activité professionnellement"]] },
];

export default function Diagnostic() {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { captureUtm(); trackEvent("diagnostic_started"); }, []);

  const steps = Q.filter((s) => !s.onlyCreator || ans.profile === "creator");
  const cur = steps[i];
  const total = steps.length;

  const pick = (val) => {
    if (cur.multi) {
      const arr = ans[cur.key] || [];
      setAns({ ...ans, [cur.key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
    } else {
      setAns({ ...ans, [cur.key]: val });
      setTimeout(() => next({ ...ans, [cur.key]: val }), 150);
    }
  };
  const answered = cur.multi ? (ans[cur.key] || []).length > 0 : !!ans[cur.key];

  const next = async (a = ans) => {
    if (i < total - 1) { setI(i + 1); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/funnel/diagnostic", { visitor_id: getVisitorId(), answers: a });
      trackEvent("diagnostic_completed", { profile: a.profile, diagnostic_score: data.score });
      sessionStorage.setItem("cehnura_diag", JSON.stringify({ ...data, profile: a.profile }));
      navigate("/diagnostic/result");
    } catch { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-zinc-100 flex flex-col items-center px-5 py-10" data-testid="diagnostic-page">
      <div className="w-full max-w-xl">
        <div className="flex items-center justify-between mb-2 text-xs text-zinc-500">
          <span>Étape {i + 1} / {total}</span>
          <button onClick={() => navigate("/")} className="hover:text-amber-400">Quitter</button>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full mb-8 overflow-hidden">
          <div className="h-full gold-gradient transition-all" style={{ width: `${((i + 1) / total) * 100}%` }} />
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold mb-6" data-testid="diagnostic-question">{cur.q}</h1>
        <div className="space-y-2">
          {cur.opts.map(([val, label]) => {
            const sel = cur.multi ? (ans[cur.key] || []).includes(val) : ans[cur.key] === val;
            return (
              <button key={val} onClick={() => pick(val)} data-testid={`diag-opt-${val}`}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${sel ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-zinc-800 bg-[#15151B] hover:border-zinc-600"}`}>
                {label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-8">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} data-testid="diag-prev"
            className="inline-flex items-center gap-1 text-sm text-zinc-400 disabled:opacity-30"><ArrowLeft className="w-4 h-4" />Précédent</button>
          {(cur.multi || i === total - 1) && (
            <button onClick={() => next()} disabled={!answered || busy} data-testid="diag-next"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl gold-gradient text-black font-semibold disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continuer <ArrowRight className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
